import type { ReactNode } from "react";
import { useState } from "react";
import { Alert, Button } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { apiFetchClient } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import { getErrorMessage } from "@/lib/api/errors";
import { getRequiredFieldErrors } from "@/shared/components/forms/validation";
import { BuiltForEgypt } from "@/shared/components/auth/AuthBrand";

type InviteAcceptFormProps = {
  token: string;
  onSuccess: () => void;
};

// Design handoff (2a): 52px inputs, 10px radius, 1.5px border, 13px/700 labels,
// warm off-white surface with white inputs. Border colour flips on focus/ error.
const INPUT_WRAP =
  "flex h-[52px] items-center gap-2 rounded-[10px] border-[1.5px] bg-white px-4 transition-colors";
const INPUT_BASE =
  "min-w-0 flex-1 border-none bg-transparent text-[15px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)]";

export function InviteAcceptForm({ token, onSuccess }: InviteAcceptFormProps) {
  const t = useTranslations();
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const passwordError = fieldErrors.password;
  const confirmPasswordError = fieldErrors.confirmPassword;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    const formData = new FormData(event.currentTarget);
    const password = String(formData.get("password") ?? "");
    const confirmPassword = String(formData.get("confirmPassword") ?? "");
    const requiredErrors = getRequiredFieldErrors(
      formData,
      ["password", "confirmPassword"],
      t(keys.errors.required),
    );

    if (Object.keys(requiredErrors).length > 0) {
      setFieldErrors(requiredErrors);
      return;
    }

    if (password.length < 8) {
      setFieldErrors({ password: t(keys.invite.passwordHint) });
      return;
    }

    if (password !== confirmPassword) {
      setFieldErrors({ confirmPassword: t(keys.invite.passwordsNoMatch) });
      return;
    }

    setSubmitting(true);
    const response = await apiFetchClient(endpoints.public.acceptInvite(token), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });

    if (!response.ok) {
      setSubmitting(false);
      if (response.status === 404) {
        setError(t(keys.invite.invalidLink));
      } else {
        setError(await getErrorMessage(response, t(keys.errors.generic)));
      }
      return;
    }

    onSuccess();
  };

  return (
    <form className="flex w-full flex-1 flex-col" onSubmit={handleSubmit}>
      {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}

      <div className="flex flex-col gap-[18px]">
        <Field label={t(keys.invite.passwordLabel)} error={passwordError}>
          <div
            className={`${INPUT_WRAP} ${
              passwordError
                ? "[border-color:var(--error-500)]"
                : "[border-color:var(--border-light)] focus-within:[border-color:var(--primary-500)]"
            }`}
          >
            <input
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder={t(keys.invite.passwordHint)}
              required
              autoComplete="new-password"
              aria-invalid={passwordError ? "true" : undefined}
              className={INPUT_BASE}
            />
            <button
              type="button"
              onClick={() => setShowPassword((value) => !value)}
              className="shrink-0 text-[13px] font-bold text-[color:var(--text-muted)]"
            >
              {showPassword ? t(keys.common.hide) : t(keys.common.show)}
            </button>
          </div>
        </Field>

        <Field label={t(keys.invite.confirmPasswordLabel)} error={confirmPasswordError}>
          <div
            className={`${INPUT_WRAP} ${
              confirmPasswordError
                ? "[border-color:var(--error-500)]"
                : "[border-color:var(--border-light)] focus-within:[border-color:var(--primary-500)]"
            }`}
          >
            <input
              name="confirmPassword"
              type={showPassword ? "text" : "password"}
              placeholder={t(keys.invite.confirmPasswordPlaceholder)}
              required
              autoComplete="new-password"
              aria-invalid={confirmPasswordError ? "true" : undefined}
              className={INPUT_BASE}
            />
          </div>
        </Field>
      </div>

      <div className="mt-auto flex flex-col gap-3.5 pt-6 lg:mt-7 lg:pt-0">
        <Button
          type="submit"
          variant="secondary"
          isLoading={submitting}
          className="h-[54px] w-full rounded-[10px] text-[16px] font-bold"
        >
          {t(keys.invite.acceptAction)}
        </Button>
        <p className="text-center text-[13px] text-[color:var(--text-muted)]">
          {t(keys.invite.supportPrompt)}{" "}
          <span className="font-bold text-[color:var(--text-primary)] underline underline-offset-2">
            {t(keys.invite.supportCta)}
          </span>
        </p>
        <div className="lg:hidden">
          <BuiltForEgypt tone="light" />
        </div>
      </div>
    </form>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-[13px] font-bold text-[color:var(--text-primary)]">
        {label}
      </span>
      {children}
      {error ? (
        <span className="text-[12px] font-medium text-[color:var(--error-500)]" aria-live="polite">
          {error}
        </span>
      ) : null}
    </label>
  );
}
